import { useState } from 'react';
import { ICON_COLOR } from '@zep-us/zus/tokens';
import { useTranslation } from 'next-i18next';
import { QuizsetFilterCriteriaSearchType } from '@/api';
import { useViewport } from '@/hooks';
import { CaretDownIcon, CheckSmallIcon, XSmallCircleColorIcon, XSmallIcon } from '@/icons';
import { PUBLIC_QUIZ_SEARCH_TYPE_TITLE_CONTENT, type PublicQuizSearchType, useSearchQuizQuery } from '@/module/public/hooks/useSearchQuizQuery';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/ui';
import { IconButton } from '@/ui_v3/components/button';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerTrigger } from '@/ui_v3/components/drawer';
import { cn } from '@/utils';
type SearchScopeOption = {
  label: string;
  value: PublicQuizSearchType;
};
const toPublicSearchScope = (searchType: PublicQuizSearchType): PublicQuizSearchType => searchType === QuizsetFilterCriteriaSearchType.AUTHOR_NAME ? QuizsetFilterCriteriaSearchType.AUTHOR_NAME : searchType === QuizsetFilterCriteriaSearchType.TITLE ? PUBLIC_QUIZ_SEARCH_TYPE_TITLE_CONTENT : searchType === PUBLIC_QUIZ_SEARCH_TYPE_TITLE_CONTENT ? PUBLIC_QUIZ_SEARCH_TYPE_TITLE_CONTENT : QuizsetFilterCriteriaSearchType.ALL;
const isFilteredSearchScope = (searchType: PublicQuizSearchType) => searchType === QuizsetFilterCriteriaSearchType.AUTHOR_NAME || searchType === PUBLIC_QUIZ_SEARCH_TYPE_TITLE_CONTENT;
const SEARCH_SCOPE_OVERLAY_Z_INDEX = 'z-[100000000]';
const SEARCH_SCOPE_TRIGGER_FRAME_CLASS_NAME = 'group flex h-[36px] min-w-[36px] items-center overflow-hidden rounded-sm text-neutral transition-colors';
const SEARCH_SCOPE_TRIGGER_TRANSPARENT_CLASS_NAME = 'bg-transparent hover:bg-gray-50 focus-within:bg-gray-50';
const SEARCH_SCOPE_TRIGGER_CONTAINED_CLASS_NAME = 'border border-[rgba(28,27,31,0.04)] bg-neutral hover:bg-[#EEF0F5] focus-within:bg-[#EEF0F5]';
const SEARCH_SCOPE_TRIGGER_BUTTON_CLASS_NAME = 'flex h-full items-center gap-3xs pl-sm pr-2xs text-body-sm-semibold transition-colors focus:outline-none';
const SEARCH_SCOPE_CLEAR_BUTTON_CLASS_NAME = 'flex h-full min-w-[28px] items-center justify-center pr-sm transition-colors focus:outline-none';
export const SearchScopeDropdown = ({
  className,
  triggerClassName
}: {
  className?: string;
  triggerClassName?: string;
}) => {
  const {
    t
  } = useTranslation(['common']);
  const {
    isMobile
  } = useViewport();
  const {
    searchType,
    handleSearchType
  } = useSearchQuizQuery();
  const [open, setOpen] = useState(false);
  const contentLabel = String(t('common.content'));
  const options: SearchScopeOption[] = [{
    label: String(t('category.all')),
    value: QuizsetFilterCriteriaSearchType.ALL
  }, {
    label: `${String(t('common.title'))}+${contentLabel}`,
    value: PUBLIC_QUIZ_SEARCH_TYPE_TITLE_CONTENT
  }, {
    label: String(t('common.creator')),
    value: QuizsetFilterCriteriaSearchType.AUTHOR_NAME
  }];
  const selectedValue = toPublicSearchScope(searchType);
  const selectedLabel = options.find(option => option.value === selectedValue)?.label ?? options[0].label;
  const isFiltered = isFilteredSearchScope(selectedValue);
  const resetSearchScope = () => {
    handleSearchType(QuizsetFilterCriteriaSearchType.ALL);
  };
  const close = () => setOpen(false);
  if (isMobile) {
    return <Drawer open={open} onOpenChange={nextOpen => {
      if (!nextOpen) close();
    }}>
        <div className={cn(SEARCH_SCOPE_TRIGGER_FRAME_CLASS_NAME, isFiltered ? SEARCH_SCOPE_TRIGGER_CONTAINED_CLASS_NAME : SEARCH_SCOPE_TRIGGER_TRANSPARENT_CLASS_NAME, className)}>
          <DrawerTrigger asChild>
            <button type="button" data-no-focus-visible onClick={() => setOpen(true)} className={cn(SEARCH_SCOPE_TRIGGER_BUTTON_CLASS_NAME, triggerClassName)}>
              <span className="truncate">{selectedLabel}</span>
              {!isFiltered && <CaretDownIcon size="xs" fill={ICON_COLOR.neutral} />}
            </button>
          </DrawerTrigger>
          {isFiltered && <button type="button" data-no-focus-visible className={SEARCH_SCOPE_CLEAR_BUTTON_CLASS_NAME} aria-label={`${selectedLabel} 필터 해제`} onPointerDown={event => {
          event.preventDefault();
          event.stopPropagation();
        }} onClick={event => {
          event.preventDefault();
          event.stopPropagation();
          resetSearchScope();
        }}>
              <XSmallIcon size="sm" fill={ICON_COLOR.neutral} />
            </button>}
        </div>
        <DrawerContent className={cn(SEARCH_SCOPE_OVERLAY_Z_INDEX, 'max-h-[50dvh] w-full rounded-t-md border-0 bg-default px-md focus:!shadow-none', 'shadow-[0_20px_32px_0_rgba(28,27,31,0.12),0_0_20px_0_rgba(28,27,31,0.08)]')} hasDim={true} hasDrawerHandle={false} onDimClick={close}>
          <DrawerHeader className="flex items-center justify-between pb-xs pt-md text-heading-md-bold">
            <DrawerTitle>{selectedLabel}</DrawerTitle>
            <IconButton variant={'transparent'} intent="neutral" size="sm" onClick={close} aria-label={`${selectedLabel} 필터 닫기`}>
              <XSmallCircleColorIcon size={'md'} />
            </IconButton>
          </DrawerHeader>
          <div className={'h-full overflow-y-auto pb-lg pt-xs [&>div]:w-full [&>div]:min-w-0'} style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          WebkitOverflowScrolling: 'auto'
        }}>
            <div>
              {options.map(option => {
              const selected = selectedValue === option.value;
              return <div key={option.value} role="menuitem" tabIndex={0} className={cn('flex h-[36px] min-h-[36px] cursor-pointer select-none items-center justify-between gap-3xs rounded-2xs px-2xs py-4xs text-body-md-medium', selected ? 'bg-strong-alternative text-strong' : 'text-default')} onClick={() => {
                handleSearchType(option.value);
                close();
              }} onKeyDown={event => {
                if (event.key !== 'Enter' && event.key !== ' ') return;
                event.preventDefault();
                handleSearchType(option.value);
                close();
              }}>
                    <span className="min-w-0 flex-1 truncate">
                      {option.label}
                    </span>
                    {selected && <CheckSmallIcon size="xs" fill={ICON_COLOR.strong} />}
                  </div>;
            })}
            </div>
          </div>
        </DrawerContent>
      </Drawer>;
  }
  return <DropdownMenu modal={false} data-sentry-element="DropdownMenu" data-sentry-component="SearchScopeDropdown" data-sentry-source-file="SearchScopeDropdown.tsx">
      <div className={cn(SEARCH_SCOPE_TRIGGER_FRAME_CLASS_NAME, isFiltered ? SEARCH_SCOPE_TRIGGER_CONTAINED_CLASS_NAME : SEARCH_SCOPE_TRIGGER_TRANSPARENT_CLASS_NAME, className)}>
        <DropdownMenuTrigger asChild data-sentry-element="DropdownMenuTrigger" data-sentry-source-file="SearchScopeDropdown.tsx">
          <button type="button" data-no-focus-visible className={cn(SEARCH_SCOPE_TRIGGER_BUTTON_CLASS_NAME, triggerClassName)}>
            <span className="truncate">{selectedLabel}</span>
            {!isFiltered && <CaretDownIcon size="xs" fill={ICON_COLOR.neutral} />}
          </button>
        </DropdownMenuTrigger>
        {isFiltered && <button type="button" data-no-focus-visible className={SEARCH_SCOPE_CLEAR_BUTTON_CLASS_NAME} aria-label={`${selectedLabel} 필터 해제`} onPointerDown={event => {
        event.preventDefault();
        event.stopPropagation();
      }} onClick={event => {
        event.preventDefault();
        event.stopPropagation();
        resetSearchScope();
      }}>
            <XSmallIcon size="sm" fill={ICON_COLOR.neutral} />
          </button>}
      </div>
      <DropdownMenuContent align="start" className={cn(
    // GNB(HeaderContainer, z-40) 아래로 깔리도록 z-30 (기본 z-50 override)
    'z-30 max-h-[530px] w-[280px] overflow-y-auto rounded-sm border border-assistive bg-default px-xs py-3xs', 'shadow-[0_4px_12px_0_rgba(28,27,31,0.12),0_0_16px_0_rgba(28,27,31,0.04)]')} data-sentry-element="DropdownMenuContent" data-sentry-source-file="SearchScopeDropdown.tsx">
        {options.map(option => {
        const selected = selectedValue === option.value;
        return <DropdownMenuItem key={option.value} className={cn('flex h-[36px] min-h-[36px] items-center justify-between rounded-2xs px-2xs py-4xs', 'text-body-md-medium transition-colors', selected ? 'bg-violet-50 text-violet-600 hover:bg-violet-100 focus:bg-violet-100' : 'text-default hover:bg-gray-100 focus:bg-gray-100')} onClick={() => {
          handleSearchType(option.value);
        }}>
              <span>{option.label}</span>
              {selected && <CheckSmallIcon size="xs" fill={ICON_COLOR.strong} />}
            </DropdownMenuItem>;
      })}
      </DropdownMenuContent>
    </DropdownMenu>;
};